import { Inmate } from '@reliance/types/models/Inmate';
import React, { useCallback, useMemo, useState } from 'react';
import { TabLoadingReporterProvider } from '../../hooks/useTabLoadingReporter';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import { useLoaderData, useRevalidator } from 'react-router-dom';
import { formatFacilityText } from '../../format/Facility';
import Header from '../view/Header';
import InmateEdit from './InmateEdit';
import InmateOperations from './InmateOperations';
import InmateNotes from './InmateNotes';
import InmateMessagingAccount from './InmateMessagingAccount';
import InmateAvAccount from './InmateAvAccount';
import InmateSubscriptions from './InmateSubscriptions';
import InmateDevices from './InmateDevices';
import InmateTextMessages from './InmateTextMessages';
import InmateCdrs from './InmateCdrs';
import InmateContacts from './InmateContacts';
import InmateSmsVoiceNumbers from './InmateSmsVoiceNumbers';
import SupportTab from './support-message/SupportTab';
import InmateChangeHistory from './InmateChangeHistory';
import InmateBookings from './InmateBookings';
import InmateDeviceUsage from '../Usage';
import InmateFaceLogin from './face-login/InmateFaceLogin';
import MergeAll from '../merge/MergeAll';
import api from '../../common/api';
import { useUser } from '../../contexts/UserContext';
import { useAlerts } from '../../alert/AlertContext';
import { formatFullName } from '../../format/FullName';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

export type InmateWithExtras = Inmate & { fullName?: string; facilityTitle?: string };

const InmateDetailView: React.FC = () => {
  const inmate = useLoaderData() as Inmate;
  const inmateTitle = useMemo(
    () => `${formatFullName(inmate)} (#${inmate.account})`,
    [inmate]
  );
  useDocumentTitle(inmateTitle);
  const revalidator = useRevalidator();
  const [recalculatingBalance, setRecalculatingBalance] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('notes');
  const alerts = useAlerts();
  const user = useUser();

  const [notesRefreshTrigger, setNotesRefreshTrigger] = useState(0);
  const [messagingAccountRefreshTrigger, setMessagingAccountRefreshTrigger] = useState(0);
  const [avAccountRefreshTrigger, setAvAccountRefreshTrigger] = useState(0);
  const [subscriptionsRefreshTrigger, setSubscriptionsRefreshTrigger] = useState(0);
  const [devicesRefreshTrigger, setDevicesRefreshTrigger] = useState(0);
  const [textMessagesRefreshTrigger, setTextMessagesRefreshTrigger] = useState(0);
  const [cdrsRefreshTrigger, setCdrsRefreshTrigger] = useState(0);
  const [contactsRefreshTrigger, setContactsRefreshTrigger] = useState(0);
  const [numbersRefreshTrigger, setNumbersRefreshTrigger] = useState(0);
  const [changesRefreshTrigger, setChangesRefreshTrigger] = useState(0);
  const [bookingsRefreshTrigger, setBookingsRefreshTrigger] = useState(0);
  const [supportMessagesRefreshTrigger, setSupportMessagesRefreshTrigger] = useState(0);
  const [deviceUsageRefreshTrigger, setDeviceUsageRefreshTrigger] = useState(0);
  const [faceLoginRefreshTrigger, setFaceLoginRefreshTrigger] = useState(0);

  const [tabLoading, setTabLoading] = useState<Record<string, boolean>>({});
  const reportTabLoading = useCallback((tabId: string, loading: boolean) => {
    setTabLoading((prev) => (prev[tabId] === loading ? prev : { ...prev, [tabId]: loading }));
  }, []);

  const bumpTabRefreshTriggers = useCallback(() => {
    setNotesRefreshTrigger((prev) => prev + 1);
    setSubscriptionsRefreshTrigger((prev) => prev + 1);
    setContactsRefreshTrigger((prev) => prev + 1);
    setDevicesRefreshTrigger((prev) => prev + 1);
    setNumbersRefreshTrigger((prev) => prev + 1);
    setTextMessagesRefreshTrigger((prev) => prev + 1);
    setMessagingAccountRefreshTrigger((prev) => prev + 1);
    setAvAccountRefreshTrigger((prev) => prev + 1);
    setCdrsRefreshTrigger((prev) => prev + 1);
    setChangesRefreshTrigger((prev) => prev + 1);
    setBookingsRefreshTrigger((prev) => prev + 1);
    setSupportMessagesRefreshTrigger((prev) => prev + 1);
    setDeviceUsageRefreshTrigger((prev) => prev + 1);
    setFaceLoginRefreshTrigger((prev) => prev + 1);
  }, []);

  const handleSave = useCallback(
    (_savedInmate: Inmate) => {
      bumpTabRefreshTriggers();
      revalidator.revalidate();
    },
    [bumpTabRefreshTriggers, revalidator],
  );

  const handleRefresh = useCallback(() => {
    bumpTabRefreshTriggers();
    revalidator.revalidate();
  }, [bumpTabRefreshTriggers, revalidator]);

  const inmateForHeader = useMemo(
    () =>
      ({
        ...inmate,
        facilityTitle: formatFacilityText(inmate.facility),
      }) as Inmate & { facilityTitle: string },
    [inmate],
  );

  const handleRecalculateBalance = useCallback(async () => {
    if (!inmate) {
      return;
    }
    if (!user || !user.hasPermission || !user.hasPermission('inmate_activity_manage')) {
      return;
    }
    setRecalculatingBalance(true);
    try {
      await api.post('/inmate-account/recalculate-balance', { inmateIds: inmate.id.toString() });
      handleRefresh();
    } catch (error) {
      alerts.error('An error occurred while recalculating balance');
    } finally {
      setRecalculatingBalance(false);
    }
  }, [inmate, user, handleRefresh]);

  const notesTabSelected = useMemo(() => activeTab === 'notes', [activeTab]);
  const messagingAccountTabSelected = useMemo(() => activeTab === 'messaging-account', [activeTab]);
  const avAccountTabSelected = useMemo(() => activeTab === 'av-account', [activeTab]);
  const subscriptionsTabSelected = useMemo(() => activeTab === 'subscriptions', [activeTab]);
  const devicesTabSelected = useMemo(() => activeTab === 'devices', [activeTab]);
  const textMessagesTabSelected = useMemo(() => activeTab === 'text-messages', [activeTab]);
  const cdrsTabSelected = useMemo(() => activeTab === 'cdrs', [activeTab]);
  const contactsTabSelected = useMemo(() => activeTab === 'contacts', [activeTab]);
  const numbersTabSelected = useMemo(() => activeTab === 'numbers', [activeTab]);
  const supportMessageTabSelected = useMemo(() => activeTab === 'support-messages', [activeTab]);
  const changesTabSelected = useMemo(() => activeTab === 'changes', [activeTab]);
  const bookingsTabSelected = useMemo(() => activeTab === 'bookings', [activeTab]);
  const deviceUsageTabSelected = useMemo(() => activeTab === 'device-usage', [activeTab]);
  const faceLoginTabSelected = useMemo(() => activeTab === 'face-login', [activeTab]);
  const mergeTabSelected = useMemo(() => activeTab === 'merge', [activeTab]);

  return (
    <>
      <Header
        inmate={inmateForHeader}
        loading={revalidator.state === 'loading'}
        recalculatingBalance={recalculatingBalance}
        onRefresh={handleRefresh}
        onRecalculateBalance={handleRecalculateBalance} />

      <div className="row">
        <div className="col-md-12 mb-3">
          <InmateEdit inmate={inmate} onSave={handleSave} />
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-md-12">
          <InmateOperations
            inmate={inmate as Inmate}
            onSuccess={handleRefresh} />
        </div>
      </div>

      <div className="row mt-3">
        <div className="col-md-12">
          <TabLoadingReporterProvider value={reportTabLoading}>
            <Tabs activeKey={activeTab} onSelect={(key) => key && setActiveTab(String(key))}>
              <Tab eventKey="notes" title={<><i className="fas fa-fw fa-lg fa-pencil-alt"></i> Notes</>}>
                <div className="jarviswidget">
                  <div className="row">
                    <div className="col-md-12 mb-3">
                      <InmateNotes
                        inmate={inmate}
                        tabSelected={notesTabSelected}
                        refreshTrigger={notesRefreshTrigger} />
                    </div>
                  </div>
                </div>
              </Tab>

              {user?.hasPermission('inmate_activity_search') && user.hasPermission('inmate_activity_manage') && (
                <Tab
                  eventKey="messaging-account"
                  title={
                    <>
                      <i className="fas fa-fw fa-lg fa-balance-scale"></i> Messaging Account
                      {tabLoading['messaging-account'] && <span> <i className="fas fa-spin fa-spinner"></i></span>}
                    </>
                  }>
                  <InmateMessagingAccount
                    inmate={inmate}
                    tabSelected={messagingAccountTabSelected}
                    refreshTrigger={messagingAccountRefreshTrigger}
                    onInmateUpdate={handleRefresh} />
                </Tab>
              )}

              {user?.hasPermission('inmate_activity_search') && user.hasPermission('inmate_activity_manage') && (
                <Tab
                  eventKey="av-account"
                  title={
                    <>
                      <i className="fas fa-fw fa-lg fa-balance-scale"></i> AV Account
                      {tabLoading['av-account'] && <span> <i className="fas fa-spin fa-spinner"></i></span>}
                    </>
                  }>
                  <InmateAvAccount
                    inmate={inmate}
                    tabSelected={avAccountTabSelected}
                    refreshTrigger={avAccountRefreshTrigger}
                    onInmateUpdate={handleRefresh} />
                </Tab>
              )}

              {user?.hasPermission('subscription_manage') && (
                <Tab
                  eventKey="subscriptions"
                  title={
                    <>
                      <i className="fas fa-fw fa-lg fa-retweet"></i> Subscriptions
                      {tabLoading.subscriptions && <span> <i className="fas fa-spin fa-spinner"></i></span>}
                    </>
                  }>
                  <div className="jarviswidget">
                    <div className="row">
                      <div className="col-md-12 mb-3">
                        <InmateSubscriptions
                          inmate={inmate}
                          tabSelected={subscriptionsTabSelected}
                          refreshTrigger={subscriptionsRefreshTrigger}
                        />
                      </div>
                    </div>
                  </div>
                </Tab>
              )}

              {user?.hasPermission('inmate_device_manage') && (
                <Tab
                  eventKey="devices"
                  title={
                    <>
                      <i className="fas fa-fw fa-lg fa-mobile"></i> Devices
                      {tabLoading.devices && <span> <i className="fas fa-spin fa-spinner"></i></span>}
                    </>
                  }>
                  <InmateDevices
                    inmate={inmate}
                    tabSelected={devicesTabSelected}
                    refreshTrigger={devicesRefreshTrigger}
                    onBlacklistChange={handleRefresh} />
                </Tab>
              )}

              {user?.hasPermission('text_message_search') && (
                <Tab
                  eventKey="text-messages"
                  title={
                    <>
                      <i className="fas fa-fw fa-lg fa-comments"></i> Texting
                      {tabLoading['text-messages'] && <span> <i className="fas fa-spin fa-spinner"></i></span>}
                    </>
                  }>
                  <InmateTextMessages
                    inmate={inmate}
                    tabSelected={textMessagesTabSelected}
                    refreshTrigger={textMessagesRefreshTrigger}
                  />
                </Tab>
              )}

              {user?.hasPermission('call_search') && (
                <Tab
                  eventKey="cdrs"
                  title={
                    <>
                      <i className="fas fa-fw fa-lg fa-phone"></i> CDRs
                      {tabLoading.cdrs && <span> <i className="fas fa-spin fa-spinner"></i></span>}
                    </>
                  }>
                  <InmateCdrs
                    inmate={inmate}
                    tabSelected={cdrsTabSelected}
                    refreshTrigger={cdrsRefreshTrigger}
                  />
                </Tab>
              )}

              {user?.hasPermission('inmate_contact_search') && (
                <Tab
                  eventKey="contacts"
                  title={
                    <>
                      <i className="fas fa-fw fa-lg fa-users"></i> Contacts
                      {tabLoading.contacts && <span> <i className="fas fa-spin fa-spinner"></i></span>}
                    </>
                  }>
                  <div className="jarviswidget">
                    <div className="row">
                      <div className="col-md-12 mb-3">
                        <InmateContacts
                          inmate={inmate}
                          tabSelected={contactsTabSelected}
                          refreshTrigger={contactsRefreshTrigger}
                        />
                      </div>
                    </div>
                  </div>
                </Tab>
              )}

              {user?.hasPermission('sms_voice_number_search') && (
                <Tab
                  eventKey="numbers"
                  title={
                    <>
                      <i className="fas fa-fw fa-lg fa-tty"></i> Numbers
                      {tabLoading.numbers && <span> <i className="fas fa-spin fa-spinner"></i></span>}
                    </>
                  }>
                  <InmateSmsVoiceNumbers
                    inmate={inmate}
                    tabSelected={numbersTabSelected}
                    refreshTrigger={numbersRefreshTrigger}
                  />
                </Tab>
              )}

              {user?.hasPermission('text_message_search') && (
                <Tab
                  eventKey="support-messages"
                  title={
                    <>
                      <i className="fas fa-fw fa-lg fa-life-ring"></i> Support Messages
                      {tabLoading['support-messages'] && <span> <i className="fas fa-spin fa-spinner"></i></span>}
                    </>
                  }>
                  <div className="jarviswidget">
                    <div className="content">
                      <div className="row">
                        <div className="col-md-12">
                          <SupportTab
                            inmate={inmate}
                            tabSelected={supportMessageTabSelected}
                            onInmateReload={handleRefresh}
                            externalRefreshTrigger={supportMessagesRefreshTrigger}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </Tab>
              )}

              <Tab
                eventKey="changes"
                title={
                  <>
                    <i className="fas fa-fw fa-lg fa-user-edit"></i> Changes
                    {tabLoading.changes && <span> <i className="fas fa-spin fa-spinner"></i></span>}
                  </>
                }>
                <InmateChangeHistory
                  inmate={inmate}
                  tabSelected={changesTabSelected}
                  refreshTrigger={changesRefreshTrigger}
                />
              </Tab>

              <Tab
                eventKey="bookings"
                title={
                  <>
                    <i className="fas fa-fw fa-lg fa-book"></i> Bookings
                    {tabLoading.bookings && <span> <i className="fas fa-spin fa-spinner"></i></span>}
                  </>
                }>
                <InmateBookings
                  inmate={inmate}
                  tabSelected={bookingsTabSelected}
                  refreshTrigger={bookingsRefreshTrigger}
                />
              </Tab>

              <Tab
                eventKey="device-usage"
                title={
                  <>
                    <i className="fas fa-fw fa-lg fa-chart-simple"></i> Device Usage
                    {tabLoading['device-usage'] && <span> <i className="fas fa-spin fa-spinner"></i></span>}
                  </>
                }>
                <InmateDeviceUsage
                  inmate={inmate}
                  tabSelected={deviceUsageTabSelected}
                  refreshTrigger={deviceUsageRefreshTrigger}
                />
              </Tab>

              {(user?.hasPermission('inmate_manage') || user?.hasPermission('face_login_sample_manage')) && (
                <Tab
                  eventKey="face-login"
                  title={
                    <>
                      <i className="fas fa-fw fa-lg fa-user-check"></i> Face Login
                      {tabLoading['face-login'] && <span> <i className="fas fa-spin fa-spinner"></i></span>}
                    </>
                  }>
                  <InmateFaceLogin
                    inmate={inmate}
                    tabSelected={faceLoginTabSelected}
                    refreshTrigger={faceLoginRefreshTrigger}
                    onInmateUpdate={handleRefresh}
                  />
                </Tab>
              )}

              {user?.hasPermission('inmate_merge') && (
                <Tab
                  eventKey="merge"
                  title={
                    <>
                      <i className="fas fa-fw fa-lg fa-code-branch"></i> Merge
                    </>
                  }>
                  <div className="jarviswidget">
                    <div className="content">
                      <div className="row">
                        <div className="col-md-12">
                          <MergeAll
                            inmate={inmate}
                            onRefresh={handleRefresh}
                            tabSelected={mergeTabSelected} />
                        </div>
                      </div>
                    </div>
                  </div>
                </Tab>
              )}
            </Tabs>
          </TabLoadingReporterProvider>
        </div>
      </div>
    </>
  );
};

export default InmateDetailView;
